import BaseApiClient from '../BaseApiClient';
import type {
  CampaignCreatePayload,
  CampaignInterface,
  CampaignLaunchPayload,
  CampaignLaunchReadinessResult,
  CampaignMessageInterface,
  CampaignMessageParams,
  CampaignMetricsInterface,
  CampaignMetricsParams,
  CampaignRecipientRowInterface,
  CampaignRecipientsParams,
  CampaignTypeInterface,
  CampaignUpdatePayload
} from '@/interfaces/Campaign.interface';
import { ListDataParams, ListDataResponse } from '../types';

const API_VERSION = { Version: '2025-11-14' };

export class ApiCampaigns extends BaseApiClient {
  /**
   * List campaigns (paginated, filterable).
   */
  async list(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<CampaignInterface>> {
    return new Promise((resolve, reject) => {
      if (searchTerm?.trim()) {
        if (!listData.filters) listData.filters = {};
        listData.filters.search = searchTerm.trim();
      }
      this.client
        .getList('/campaigns', listData, { headers: API_VERSION })
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data ?? [],
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * List campaign types for filters and forms.
   */
  async listTypes(): Promise<CampaignTypeInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/campaign-types', { headers: API_VERSION })
        .then((res) => {
          if (res.data) {
            resolve(res.data.data ?? []);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get a single campaign by uuid.
   */
  async show(uuid: string): Promise<CampaignInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/campaigns/${uuid}`, { headers: API_VERSION })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Create a campaign.
   */
  async create(payload: CampaignCreatePayload): Promise<CampaignInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post('/campaigns', payload, { headers: API_VERSION })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Update a campaign by uuid.
   */
  async update(
    uuid: string,
    payload: CampaignUpdatePayload
  ): Promise<CampaignInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/campaigns/${uuid}`, payload, { headers: API_VERSION })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Assess whether a campaign is ready to launch (persisted state only).
   */
  async getLaunchReadiness(uuid: string): Promise<CampaignLaunchReadinessResult> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/campaigns/${uuid}/launch-readiness`, { headers: API_VERSION })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Launch a campaign (promotes workflows; launched_at is set on first workflow execution).
   */
  async launch(
    uuid: string,
    payload: CampaignLaunchPayload
  ): Promise<CampaignInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/campaigns/${uuid}/launch`, payload, { headers: API_VERSION })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Pause an active or scheduled campaign.
   */
  async pause(uuid: string): Promise<CampaignInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/campaigns/${uuid}/pause`, {}, { headers: API_VERSION })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Resume a paused campaign.
   */
  async resume(uuid: string): Promise<CampaignInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/campaigns/${uuid}/resume`, {}, { headers: API_VERSION })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Duplicate a campaign (copies CRM data and workflows as a new draft).
   */
  async duplicate(uuid: string): Promise<CampaignInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/campaigns/${uuid}/duplicate`, {}, { headers: API_VERSION })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get aggregated delivery/engagement metrics for a campaign.
   */
  async getMetrics(
    uuid: string,
    params?: CampaignMetricsParams
  ): Promise<CampaignMetricsInterface> {
    return new Promise((resolve, reject) => {
      const queryParams: Record<string, string> = {};
      if (params?.scope != null) {
        queryParams.scope = params.scope;
      }
      if (params?.followUpWorkflowId != null) {
        queryParams.followUpWorkflowId = params.followUpWorkflowId;
      }

      this.client
        .get(`/campaigns/${uuid}/metrics`, {
          headers: API_VERSION,
          params: queryParams
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get message-level metrics for a single recipient within a campaign.
   */
  async getMessage(
    uuid: string,
    params: CampaignMessageParams
  ): Promise<CampaignMessageInterface> {
    return new Promise((resolve, reject) => {
      const encodedRecipient = encodeURIComponent(params.recipient);
      const queryParams: Record<string, string> = {};
      if (params.followUpWorkflowId != null) {
        queryParams.followUpWorkflowId = params.followUpWorkflowId;
      }

      this.client
        .get(`/campaigns/${uuid}/message/${encodedRecipient}`, {
          headers: API_VERSION,
          params: queryParams
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get paginated campaign recipients with audience fields and PB engagement.
   */
  async getRecipients(
    uuid: string,
    listData: ListDataParams,
    searchTerm?: string,
    params?: CampaignRecipientsParams
  ): Promise<ListDataResponse<CampaignRecipientRowInterface>> {
    return new Promise((resolve, reject) => {
      if (searchTerm?.trim()) {
        if (!listData.filters) listData.filters = {};
        listData.filters.value = searchTerm.trim();
      }

      const queryParams: Record<string, string> = {};
      if (params?.scope != null) {
        queryParams.scope = params.scope;
      }
      if (params?.followUpWorkflowId != null) {
        queryParams.followUpWorkflowId = params.followUpWorkflowId;
      }

      this.client
        .getList(`/campaigns/${uuid}/recipients`, listData, {
          headers: API_VERSION,
          params: queryParams
        })
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data ?? [],
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Soft-delete a campaign by uuid.
   */
  async delete(uuid: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .delete(`/campaigns/${uuid}`, { headers: API_VERSION })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  }
}

export { campaignsQueryKeys } from './query-keys';
export type {
  CampaignsListQueryParams,
  CampaignMetricsQueryParams,
  CampaignMessageQueryParams,
  CampaignRecipientsQueryParams
} from './query-keys';
export {
  useCampaignsListQuery,
  useCampaignQuery,
  useCampaignLaunchReadinessQuery,
  useCampaignMetricsQuery,
  useCampaignMessageQuery,
  useCampaignRecipientsQuery
} from './use-campaigns-queries';
export {
  useCampaignCreateMutation,
  useCampaignUpdateMutation,
  useCampaignLaunchMutation,
  useCampaignPauseMutation,
  useCampaignResumeMutation,
  useCampaignDuplicateMutation,
  useCampaignDeleteMutation
} from './use-campaigns-mutations';
export type {
  CampaignUpdateVariables,
  CampaignLaunchVariables,
  CampaignPauseVariables,
  CampaignResumeVariables,
  CampaignDuplicateVariables,
  CampaignDeleteVariables
} from './use-campaigns-mutations';
